/*
  Featured Guide section interactions:

  - <featured-guide-media>: the image pans a few pixels toward the cursor
    as it moves within the frame (magnetic parallax feel). The <img> is
    pre-scaled up via CSS (see section-featured-guide.css) so the pan
    offset never reveals empty space at the frame edges.

  - <featured-guide-card>: on connect, randomizes the position/size/timing
    of the ambient sparkles scattered near the heading and the button, baked
    in once as inline styles (same approach as the Sky This Month card
    sparkles) - CSS handles the actual fade-in/out on :hover.
*/
class FeaturedGuideMedia extends HTMLElement {
  connectedCallback() {
    this.image = this.querySelector('img');
    if (!this.image) return;

    this.maxOffset = 4; // px - image pans up to this far toward the cursor

    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerLeave = this.handlePointerLeave.bind(this);

    this.addEventListener('mousemove', this.handlePointerMove);
    this.addEventListener('mouseleave', this.handlePointerLeave);
  }

  handlePointerMove(event) {
    const rect = this.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const xPercent = (event.clientX - rect.left) / rect.width;
    const yPercent = (event.clientY - rect.top) / rect.height;
    const offsetX = (xPercent - 0.5) * 2 * this.maxOffset;
    const offsetY = (yPercent - 0.5) * 2 * this.maxOffset;

    this.image.style.setProperty('--pan-x', `${offsetX.toFixed(1)}px`);
    this.image.style.setProperty('--pan-y', `${offsetY.toFixed(1)}px`);
  }

  handlePointerLeave() {
    this.image.style.setProperty('--pan-x', '0px');
    this.image.style.setProperty('--pan-y', '0px');
  }
}

class FeaturedGuideCard extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-guide-sparkle]').forEach((sparkle, index) => {
      const baseDelay = 0.05 * ((index % 3) + 1);
      sparkle.style.cssText = FeaturedGuideCard.randomSparkleStyle(baseDelay);
    });
  }

  // Scatters a sparkle loosely around its positioning wrapper (percentage-
  // based so it scales with the wrapper regardless of heading length or
  // viewport width), with a staggered transition/animation delay matching
  // the Sky This Month card sparkle pattern.
  static randomSparkleStyle(baseDelaySeconds) {
    const topPercent = -15 + Math.random() * 130; // -15% to 115%
    const leftPercent = -10 + Math.random() * 120; // -10% to 110%
    const size = 4 + Math.random() * 2; // 4-6px
    const jitter = Math.random() * 0.1 - 0.05; // +/- 0.05s
    const transitionDelay = Math.max(0, baseDelaySeconds + jitter);
    const animationDelay = transitionDelay + 0.25; // let the fade-in transition finish first

    return [
      `top: ${topPercent.toFixed(1)}%`,
      `left: ${leftPercent.toFixed(1)}%`,
      `width: ${size.toFixed(1)}px`,
      `height: ${size.toFixed(1)}px`,
      `margin-top: -${(size / 2).toFixed(1)}px`,
      `margin-left: -${(size / 2).toFixed(1)}px`,
      `transition-delay: ${transitionDelay.toFixed(2)}s`,
      `animation-delay: ${animationDelay.toFixed(2)}s`
    ].join('; ');
  }
}

customElements.define('featured-guide-media', FeaturedGuideMedia);
customElements.define('featured-guide-card', FeaturedGuideCard);
